import { area } from '@turf/turf'
import type { ContinuousStop } from 'api-validations/stitch'
import { stringToNumber } from 'api-validations/zod-extra'
import type { Feature, FeatureCollection, MultiPolygon, Polygon } from 'geojson'
import { WKT } from 'ol/format'
import type { Geometry as OlGeometry } from 'ol/geom'
import { GeometryCollection, MultiPolygon as OlMultiPolygon, Polygon as OlPolygon } from 'ol/geom'
import { Enum, Geo, isNil, type WeightedValue, zoneIndexOf } from 'utils'
import { z } from 'zod'

import { legendFromGradient } from '../legend-editor/useUserFacingLegend'
import { SoilSurveyAttribute } from '../map-state-store'
import {
  SOIL_BAND_COUNT,
  SOIL_GRADIENT,
  soilBandColor,
  type SoilValueRange
} from '../soil-sample-scale'
import { ZONE_PALETTE } from '../zone-colors'

/** USDA's drainage classes, driest first. */
export enum DrainageClass {
  ExcessivelyDrained = 'excessivelyDrained',
  SomewhatExcessivelyDrained = 'somewhatExcessivelyDrained',
  WellDrained = 'wellDrained',
  ModeratelyWellDrained = 'moderatelyWellDrained',
  SomewhatPoorlyDrained = 'somewhatPoorlyDrained',
  PoorlyDrained = 'poorlyDrained',
  VeryPoorlyDrained = 'veryPoorlyDrained',
  Subaqueous = 'subaqueous'
}

export type SoilMapUnit = {
  name: string
  drainageClass: DrainageClass | null
  nccpi: number | null
}

export type SoilSurvey = FeatureCollection<Polygon | MultiPolygon, SoilMapUnit>

export type SoilSurveyZone = { fillColor: string }

export type UnratedLegendEntry = { kind: 'Unrated'; color: string }

export type SoilSurveyLegendEntry =
  | { kind: 'SoilType'; name: string; color: string }
  | { kind: 'DrainageClass'; drainageClass: DrainageClass; color: string }
  | UnratedLegendEntry

export type SoilSurveyLegend =
  | { kind: 'Categorical'; entries: SoilSurveyLegendEntry[] }
  | {
      kind: 'Continuous'
      bands: ContinuousStop[]
      range: SoilValueRange
      unrated: UnratedLegendEntry | null
    }

const SDA_URL = 'https://sdmdataaccess.nrcs.usda.gov/tabular/post.rest'
const NCCPI_RULE = 'NCCPI - National Commodity Crop Productivity Index (Ver 3.0)'

const SDA_TIMEOUT_MS = 30_000

const surveyRowWireSchema = z.tuple([
  z.string(),
  z.string().nullable(),
  stringToNumber.nullable(),
  z.string()
])

const surveyRowSchema = z.codec(
  surveyRowWireSchema,
  z.object({
    name: z.string(),
    drainageClass: z.string().nullable(),
    nccpi: z.number().nullable(),
    wkt: z.string()
  }),
  {
    decode: ([name, drainageClass, nccpi, wkt]) => ({ name, drainageClass, nccpi, wkt }),
    encode: ({ name, drainageClass, nccpi, wkt }): z.output<typeof surveyRowWireSchema> => [
      name,
      drainageClass,
      nccpi,
      wkt
    ]
  }
)

export type SoilSurveyRow = z.output<typeof surveyRowSchema>

const surveyResponseSchema = z.object({ Table: z.array(surveyRowSchema).optional() })

const surveyQuery = (wkt: string): string => {
  const boundary = `geometry::STGeomFromText('${wkt}', 4326).MakeValid()`
  return `
    SELECT ma.muname, ma.drclassdcd,
      (SELECT CAST(SUM(c.comppct_r * ci.interphr) / NULLIF(SUM(c.comppct_r), 0) AS decimal(5, 3))
         FROM component c JOIN cointerp ci ON ci.cokey = c.cokey
         WHERE c.mukey = mp.mukey AND ci.mrulename = '${NCCPI_RULE}' AND ci.ruledepth = 0
           AND ci.interphr IS NOT NULL AND c.comppct_r IS NOT NULL),
      mp.mupolygongeo.STIntersection(${boundary}).STAsText()
    FROM mupolygon mp JOIN muaggatt ma ON ma.mukey = mp.mukey
    WHERE mp.mupolygongeo.STIntersects(${boundary}) = 1`
}

const wktFormat = new WKT()

const boundaryWkt = (geometry: Polygon | MultiPolygon): string =>
  wktFormat.writeGeometry(
    geometry.type === 'Polygon'
      ? new OlPolygon(geometry.coordinates)
      : new OlMultiPolygon(geometry.coordinates)
  )

export const fetchSoilSurvey = async ({
  boundary,
  signal
}: {
  boundary: Feature<Polygon | MultiPolygon>
  signal: AbortSignal
}): Promise<SoilSurvey> => {
  const response = await fetch(SDA_URL, {
    method: 'POST',
    body: new URLSearchParams({
      query: surveyQuery(boundaryWkt(boundary.geometry)),
      format: 'JSON'
    }),
    signal: AbortSignal.any([signal, AbortSignal.timeout(SDA_TIMEOUT_MS)])
  })
  if (!response.ok) {
    throw new Error(`Soil Data Access request failed (${response.status})`)
  }
  return toSoilSurvey(parseSurveyRows(await response.text()))
}

const parseSurveyRows = (body: string): SoilSurveyRow[] => {
  let json: unknown
  try {
    json = JSON.parse(body)
  } catch {
    throw new Error(`Soil Data Access returned a non-JSON response: ${body.slice(0, 200)}`)
  }
  return surveyResponseSchema.parse(json).Table ?? []
}

const DRAINAGE_CLASS_BY_USDA_NAME: Record<string, DrainageClass | undefined> = {
  'Excessively drained': DrainageClass.ExcessivelyDrained,
  'Somewhat excessively drained': DrainageClass.SomewhatExcessivelyDrained,
  'Well drained': DrainageClass.WellDrained,
  'Moderately well drained': DrainageClass.ModeratelyWellDrained,
  'Somewhat poorly drained': DrainageClass.SomewhatPoorlyDrained,
  'Poorly drained': DrainageClass.PoorlyDrained,
  'Very poorly drained': DrainageClass.VeryPoorlyDrained,
  Subaqueous: DrainageClass.Subaqueous
}

export const toSoilSurvey = (rows: readonly SoilSurveyRow[]): SoilSurvey => ({
  type: 'FeatureCollection',
  features: rows.flatMap(({ name, drainageClass, nccpi, wkt }) => {
    const geometry = polygonalGeometry(wktFormat.readGeometry(wkt))
    if (geometry === null) return []
    return [
      {
        type: 'Feature' as const,
        geometry,
        properties: {
          name,
          drainageClass:
            drainageClass === null ? null : (DRAINAGE_CLASS_BY_USDA_NAME[drainageClass] ?? null),
          nccpi
        }
      }
    ]
  })
})

const polygonsOf = (geometry: OlGeometry): OlPolygon[] => {
  if (geometry instanceof OlPolygon) return [geometry]
  if (geometry instanceof OlMultiPolygon) return geometry.getPolygons()
  if (geometry instanceof GeometryCollection) {
    return geometry.getGeometriesArray().flatMap(polygonsOf)
  }
  return []
}

const polygonalGeometry = (geometry: OlGeometry): Polygon | MultiPolygon | null => {
  const polygons = polygonsOf(geometry)
  const [first, ...rest] = polygons
  if (first === undefined) return null
  if (rest.length === 0) return { type: 'Polygon', coordinates: first.getCoordinates() }
  return { type: 'MultiPolygon', coordinates: polygons.map((polygon) => polygon.getCoordinates()) }
}

const UNRATED_GRAY = '#9CA3AF'
const UNRATED_ENTRY: UnratedLegendEntry = { kind: 'Unrated', color: UNRATED_GRAY }

const DRAINAGE_COLOR: Record<DrainageClass, string> = {
  [DrainageClass.ExcessivelyDrained]: '#F7FBFF',
  [DrainageClass.SomewhatExcessivelyDrained]: '#DEEBF7',
  [DrainageClass.WellDrained]: '#C6DBEF',
  [DrainageClass.ModeratelyWellDrained]: '#9ECAE1',
  [DrainageClass.SomewhatPoorlyDrained]: '#6BAED6',
  [DrainageClass.PoorlyDrained]: '#4292C6',
  [DrainageClass.VeryPoorlyDrained]: '#2171B5',
  [DrainageClass.Subaqueous]: '#084594'
}

const NCCPI_RANGE: SoilValueRange = { min: 0, max: 1 }

type Zone = { key: string; color: string }

const UNRATED_ZONE: Zone = { key: 'unrated', color: UNRATED_GRAY }

type Styling = {
  zoneOf: (unit: SoilMapUnit) => Zone
  legend: SoilSurveyLegend
}

export type CategoricalSoilSurveyAttribute =
  | SoilSurveyAttribute.SoilType
  | SoilSurveyAttribute.DrainageClass

export const isCategoricalSoilSurveyAttribute = (
  attribute: SoilSurveyAttribute
): attribute is CategoricalSoilSurveyAttribute => Object.hasOwn(CLASSES, attribute)

type Classes = {
  entryOf: (unit: SoilMapUnit) => SoilSurveyLegendEntry
  entries: SoilSurveyLegendEntry[]
}

export const soilSurveyEntryKey = (entry: SoilSurveyLegendEntry): string => {
  switch (entry.kind) {
    case 'SoilType':
      return entry.name
    case 'DrainageClass':
      return entry.drainageClass
    case 'Unrated':
      return entry.kind
  }
}

const soilTypeClasses = (units: readonly SoilMapUnit[]): Classes => {
  const names = [...new Set(units.map((unit) => unit.name))].toSorted((a, b) => a.localeCompare(b))
  const entries = names.map((name, index) => ({
    kind: 'SoilType' as const,
    name,
    color: ZONE_PALETTE[index % ZONE_PALETTE.length]
  }))
  const entryByName = new Map(entries.map((entry) => [entry.name, entry]))

  return {
    entryOf: (unit) => entryByName.get(unit.name) ?? UNRATED_ENTRY,
    entries
  }
}

const drainageClasses = (units: readonly SoilMapUnit[]): Classes => {
  const present = Enum.values(DrainageClass).filter((drainageClass) =>
    units.some((unit) => unit.drainageClass === drainageClass)
  )
  const entries = present.map((drainageClass) => ({
    kind: 'DrainageClass' as const,
    drainageClass,
    color: DRAINAGE_COLOR[drainageClass]
  }))
  const hasUnrated = units.some((unit) => unit.drainageClass === null)

  return {
    entryOf: (unit) =>
      unit.drainageClass === null
        ? UNRATED_ENTRY
        : {
            kind: 'DrainageClass',
            drainageClass: unit.drainageClass,
            color: DRAINAGE_COLOR[unit.drainageClass]
          },
    entries: hasUnrated ? [...entries, UNRATED_ENTRY] : entries
  }
}

const CLASSES: Record<CategoricalSoilSurveyAttribute, (units: readonly SoilMapUnit[]) => Classes> =
  {
    [SoilSurveyAttribute.SoilType]: soilTypeClasses,
    [SoilSurveyAttribute.DrainageClass]: drainageClasses
  }

const categoricalStyling = ({ entryOf, entries }: Classes): Styling => ({
  zoneOf: (unit) => {
    const entry = entryOf(unit)
    return { key: soilSurveyEntryKey(entry), color: entry.color }
  },
  legend: { kind: 'Categorical', entries }
})

const nccpiStyling = (units: readonly SoilMapUnit[]): Styling => {
  const bands = legendFromGradient(SOIL_GRADIENT, SOIL_BAND_COUNT, {
    low: NCCPI_RANGE.min,
    high: NCCPI_RANGE.max
  })
  const hasUnrated = units.some((unit) => unit.nccpi === null)

  return {
    zoneOf: (unit) => {
      if (unit.nccpi === null) return UNRATED_ZONE
      const color = soilBandColor({ value: unit.nccpi, bands })
      return color === undefined ? UNRATED_ZONE : { key: color, color }
    },
    legend: {
      kind: 'Continuous',
      bands,
      range: NCCPI_RANGE,
      unrated: hasUnrated ? UNRATED_ENTRY : null
    }
  }
}

const STYLING: Record<SoilSurveyAttribute, (units: readonly SoilMapUnit[]) => Styling> = {
  [SoilSurveyAttribute.SoilType]: (units) => categoricalStyling(soilTypeClasses(units)),
  [SoilSurveyAttribute.DrainageClass]: (units) => categoricalStyling(drainageClasses(units)),
  [SoilSurveyAttribute.Nccpi]: nccpiStyling
}

const stylingOf = ({
  survey,
  attribute
}: {
  survey: SoilSurvey
  attribute: SoilSurveyAttribute
}): Styling => STYLING[attribute](survey.features.map((unit) => unit.properties))

export const soilSurveyLegend = (args: {
  survey: SoilSurvey
  attribute: SoilSurveyAttribute
}): SoilSurveyLegend => stylingOf(args).legend

export const soilSurveyZones = (args: {
  survey: SoilSurvey
  attribute: SoilSurveyAttribute
}): FeatureCollection<Polygon | MultiPolygon, SoilSurveyZone> => {
  const { zoneOf } = stylingOf(args)

  const groups = new Map<string, { color: string; units: SoilSurvey['features'] }>()
  for (const unit of args.survey.features) {
    const { key, color } = zoneOf(unit.properties)
    const group = groups.get(key) ?? { color, units: [] }
    group.units.push(unit)
    groups.set(key, group)
  }

  return {
    type: 'FeatureCollection',
    features: [...groups.values()].flatMap(({ color, units }) =>
      coalesced(units).map((geometry) => ({
        type: 'Feature' as const,
        geometry,
        properties: { fillColor: color }
      }))
    )
  }
}

export type SoilSurveyClass = {
  entry: SoilSurveyLegendEntry
  features: Feature<Polygon | MultiPolygon>[]
}

export const soilSurveyClasses = ({
  survey,
  attribute
}: {
  survey: SoilSurvey
  attribute: CategoricalSoilSurveyAttribute
}): SoilSurveyClass[] => {
  const { entryOf, entries } = CLASSES[attribute](survey.features.map((unit) => unit.properties))
  const unitsByKey = Map.groupBy(survey.features, (unit) =>
    soilSurveyEntryKey(entryOf(unit.properties))
  )

  return entries.map((entry) => ({
    entry,
    features: asFeatures(coalesced(unitsByKey.get(soilSurveyEntryKey(entry)) ?? []))
  }))
}

const ratedUnits = (survey: SoilSurvey) =>
  survey.features.flatMap((unit) =>
    unit.properties.nccpi === null ? [] : [{ unit, nccpi: unit.properties.nccpi }]
  )

export const nccpiValues = (survey: SoilSurvey): WeightedValue[] =>
  ratedUnits(survey).map(({ unit, nccpi }) => ({ value: nccpi, weight: area(unit) }))

export const zoneNccpi = ({
  survey,
  breaks
}: {
  survey: SoilSurvey
  breaks: readonly number[]
}): Feature<Polygon | MultiPolygon>[][] => {
  const unitsByZone = Map.groupBy(ratedUnits(survey), ({ nccpi }) =>
    zoneIndexOf({ value: nccpi, breaks })
  )

  return Array.from({ length: breaks.length + 1 }, (_, index) =>
    asFeatures(coalesced((unitsByZone.get(index) ?? []).map(({ unit }) => unit)))
  )
}

const asFeatures = (geometries: (Polygon | MultiPolygon)[]): Feature<Polygon | MultiPolygon>[] =>
  geometries.map((geometry) => ({ type: 'Feature', geometry, properties: {} }))

const coalesced = (units: SoilSurvey['features']): (Polygon | MultiPolygon)[] => {
  try {
    const merged = Geo.normalizeCollection({ type: 'FeatureCollection', features: units })
    return isNil(merged) ? units.map((unit) => unit.geometry) : [merged.geometry]
  } catch {
    return units.map((unit) => unit.geometry)
  }
}
